// Next 16 client instrumentation — runs once after the HTML loads and BEFORE
// React hydration (node_modules/next/dist/docs/01-app/03-api-reference/
// 03-file-conventions/instrumentation-client.md), the earliest safe point to
// start analytics so no early events are missed.
//
// PostHog init is a no-op without NEXT_PUBLIC_POSTHOG_KEY, so this file is inert
// in dev / test / CI. The try/catch guarantees analytics setup can never break
// app boot.

import {
  captureException,
  EVENTS,
  initPostHog,
  safeErrorMessage,
  track,
} from '@/lib/analytics';
import { resolveCampaignAngle } from '@/lib/campaign/resolveAngle';
import { initRedditPixel, redditTrack } from '@/lib/redditPixel';

let lastClientErrorKey = '';
let lastClientErrorAt = 0;

function reportClientError(error: unknown, source: string, digest?: string): void {
  const message = safeErrorMessage(error);
  const key = `${source}:${message}:${digest ?? ''}`;
  const now = Date.now();
  if (key === lastClientErrorKey && now - lastClientErrorAt < 3000) return;
  lastClientErrorKey = key;
  lastClientErrorAt = now;
  track(EVENTS.client_error, { message, source, digest });
  captureException(error, { source, digest });
}

try {
  initPostHog();
} catch {
  // Swallow — a broken analytics init must not stop the app from rendering.
}

try {
  // Pin this tab's acquisition angle from `?a=` and register it as a PostHog
  // super property, so every event of the session says which ad it came from.
  //
  // HERE rather than in the component that renders the hook, for two reasons.
  // It runs before hydration, so even the session's FIRST `$pageview` carries
  // `campaign_angle` — resolving it inside React would leave that first event
  // untagged and quietly understate the top of every funnel. And it is
  // page-agnostic: an ad pointed at `/landing`, or at a room a signed-in user
  // opens straight into the editor, is tagged just the same, even though
  // neither renders a hook screen.
  //
  // No-ops for an organic visitor and for an unrecognised angle.
  resolveCampaignAngle();
} catch {
  // Same contract as the init above: acquisition tagging is never worth a
  // blank page.
}

try {
  // Reddit Ads pixel: landing PageVisit + the ad click id. Inert off the
  // production domain and inside the native app (lib/redditPixel.ts).
  initRedditPixel();
} catch {
  // Ad attribution is never worth a blank page either.
}

/** Next calls this on every client-side navigation; the pixel only sees full
 *  loads on its own, so SPA page views are forwarded by hand. */
export function onRouterTransitionStart(): void {
  redditTrack('PageVisit');
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    reportClientError(event.error ?? event.message, 'window.onerror');
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason, 'unhandledrejection');
  });
}
